import { useState } from 'react';

import { BsCheckCircleFill, BsXCircle } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';

import { useIntegrationsList } from '../../../models/integrations/queries';
import { resolveChannelIcon } from '../../../utils/channelIcons';

import AircallConfigModal from './integrations/AircallConfigModal';
import CalendarConfigModal from './integrations/CalendarConfigModal';

/**
 * Registry that maps channel slugs to their config modal.
 * Adding a new integration = adding one entry here + the modal component.
 */
const INTEGRATION_MODALS = {
	phone_aircall: AircallConfigModal,
	calendar: CalendarConfigModal,
};

const INTEGRATION_DESCRIPTIONS = {
	phone_aircall: 'Synchronisiere Anrufe automatisch als CRM-Aktivitäten.',
	calendar: 'Synchronisiere Meetings mit Google Calendar oder Microsoft 365.',
};

const IntegrationsSettingsTab = () => {
	const { data: integrations = [], isLoading } = useIntegrationsList();
	const calendarConnections = integrations.calendarConnections ?? [];
	const [activeSlug, setActiveSlug] = useState(null);

	if (isLoading) return <p className="text-bgBase-800">Integrationen werden geladen …</p>;

	const available = integrations.filter((i) => i.isActive);
	const comingSoon = integrations.filter((i) => !i.isActive);

	const activeIntegration = integrations.find((i) => i.slug === activeSlug);
	const ActiveModal = activeSlug ? INTEGRATION_MODALS[activeSlug] : null;

	return (
		<div className="space-y-6">
			<div>
				<h2 className="text-sm font-semibold text-text mb-3">Verfügbare Integrationen</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{available.map((integration) => {
						const Icon = resolveChannelIcon(integration);
						const description = INTEGRATION_DESCRIPTIONS[integration.slug] ?? '';

						// Calendar uses userServiceConnections, not crmChannelConfigs
						const isCalendar = integration.slug === 'calendar';
						const isConnected = isCalendar ? calendarConnections.length > 0 : integration.isConnected;
						const connectedLabel =
							isCalendar && calendarConnections.length > 0
								? `${calendarConnections.length} Kalender`
								: integration.providerCompanyName || '';

						return (
							<Card key={integration.id} className="p-5 flex flex-col justify-between">
								<div>
									<div className="flex items-center gap-3 mb-3">
										<div className="w-10 h-10 rounded-control bg-primary-100 flex items-center justify-center">
											<Icon className="w-5 h-5 text-primary-700" />
										</div>
										<div>
											<h3 className="text-sm font-semibold text-text">
												{integration.displayName}
											</h3>
											{isConnected && (
												<span className="flex items-center gap-1 text-xs text-success-700 font-medium">
													<BsCheckCircleFill className="w-3 h-3" />
													Verbunden{connectedLabel ? ` · ${connectedLabel}` : ''}
												</span>
											)}
											{!isConnected && (
												<span className="flex items-center gap-1 text-xs text-bgBase-800 font-medium">
													<BsXCircle className="w-3 h-3" />
													Nicht verbunden
												</span>
											)}
										</div>
									</div>
									<p className="text-xs text-bgBase-800 mb-4">{description}</p>
								</div>
								<Button variant="outline" fullWidth onClick={() => setActiveSlug(integration.slug)}>
									Konfigurieren
								</Button>
							</Card>
						);
					})}
				</div>
			</div>

			{comingSoon.length > 0 && (
				<div>
					<h2 className="text-sm font-semibold text-text mb-3">Coming soon</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{comingSoon.map((integration) => {
							const ComingSoonIcon = resolveChannelIcon(integration);
							return (
								<div
									key={integration.id}
									className="rounded-card border-2 border-dashed border-bgBase-500 bg-bgBase-300 p-5 flex items-center gap-3"
								>
									<div className="w-10 h-10 rounded-control bg-bgBase-400 flex items-center justify-center">
										<ComingSoonIcon className="w-5 h-5 text-bgBase-800" />
									</div>
									<div>
										<p className="text-sm font-medium text-text">{integration.displayName}</p>
										<p className="text-xs text-bgBase-800">Demnächst verfügbar</p>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}

			{ActiveModal && (
				<ActiveModal
					isOpen={Boolean(activeSlug)}
					onClose={() => setActiveSlug(null)}
					integration={activeIntegration}
				/>
			)}
		</div>
	);
};

export default IntegrationsSettingsTab;
